import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../helpers/redux';
import { RootState } from '../../../../app/store';
import { getList as getProjectList } from '../../../master/project/project.reducer';
import { getList as getDistrictList } from '../../../master/district/district.reducer';
import { getList as getJobTypeList } from '../../../master/jobType/jobType.reducer';
import { getList as getJobStatusList } from '../../../master/jobStatus/jobStatus.reducer';
import { getList as getPoleTypeList } from '../../../master/poleType/poleType.reducer';
import { getList as getPoleStatusList } from '../../../master/poleStatus/poleStatus.reducer';
import Select from 'react-select';

interface PoleManagementFiltersProps {
    onFilterChange?: (filters: any) => void;
}

const PoleManagementFilters = ({
    onFilterChange,
}: PoleManagementFiltersProps) => {
    const dispatch = useAppDispatch();

    const projects = useAppSelector(
        (state: RootState) => state.project.list || [],
    );
    const districts = useAppSelector(
        (state: RootState) => state.district.list || [],
    );
    const jobTypes = useAppSelector(
        (state: RootState) => state.jobType.list || [],
    );
    const jobStatuses = useAppSelector(
        (state: RootState) => state.jobStatus.list || [],
    );
    const poleTypeOptions = useAppSelector(
        (state: RootState) => state.poleType.options || [],
    );
    const poleStatusOptions = useAppSelector(
        (state: RootState) => state.poleStatus.options || [],
    );

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [project, setProject] = useState('');
    const [district, setDistrict] = useState('');
    const [jobType, setJobType] = useState('');
    const [status, setStatus] = useState('Complete');
    const [jobNumber, setJobNumber] = useState('');
    const [otherNumber, setOtherNumber] = useState('');
    const [completeStartDate, setCompleteStartDate] = useState('');
    const [completeEndDate, setCompleteEndDate] = useState('');
    const [selectedPoleTypes, setSelectedPoleTypes] = useState<any[]>([]);
    const [selectedPoleStatuses, setSelectedPoleStatuses] = useState<any[]>([]);

    useEffect(() => {
        dispatch(getProjectList({ skip: 0, take: 1000 }));
        dispatch(getDistrictList({ skip: 0, take: 1000 }));
        dispatch(getJobTypeList({ skip: 0, take: 1000 }));
        dispatch(getJobStatusList({ skip: 0, take: 1000 }));
        dispatch(getPoleTypeList({ skip: 0, take: 1000 }));
        dispatch(getPoleStatusList({ skip: 0, take: 1000 }));
    }, [dispatch]);

    const handleSearch = () => {
        if (onFilterChange) {
            onFilterChange({
                startRequestDate: startDate || undefined,
                endRequestDate: endDate || undefined,
                project_name: project || undefined,
                district: district || undefined,
                kind_job: jobType || undefined,
                status: status || undefined,
                number_job: jobNumber || undefined,
                other_number: otherNumber || undefined,
                completeStartDate: completeStartDate || undefined,
                completeEndDate: completeEndDate || undefined,
                pole_types: selectedPoleTypes.length > 0 ? selectedPoleTypes.map(x => x.value).join(',') : undefined,
                pole_statuses: selectedPoleStatuses.length > 0 ? selectedPoleStatuses.map(x => x.value).join(',') : undefined,
            });
        }
    };

    const handleReset = () => {
        setEndDate('');
        setStartDate('');
        setProject('');
        setDistrict('');
        setJobType('');
        setStatus('Complete');
        setJobNumber('');
        setOtherNumber('');
        setCompleteStartDate('');
        setCompleteEndDate('');
        setSelectedPoleTypes([]);
        setSelectedPoleStatuses([]);
        if (onFilterChange) {
            onFilterChange({
                status: 'Complete',
                startRequestDate: undefined,
                endRequestDate: undefined,
            });
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-4 w-full flex flex-wrap gap-4 items-end">
            <div className="flex flex-col gap-1 w-full md:w-[320px] flex-shrink-0">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Request Date
                </label>
                <div className="flex items-center gap-2 text-sm">
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded-md p-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 outline-none w-full"
                    />
                    <span className="text-gray-500">-</span>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded-md p-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 outline-none w-full"
                    />
                </div>
            </div>

            <div className="flex flex-col gap-1 w-full md:w-[320px] flex-shrink-0">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Complete Date
                </label>
                <div className="flex items-center gap-2 text-sm">
                    <input
                        type="date"
                        value={completeStartDate}
                        onChange={(e) => setCompleteStartDate(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded-md p-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 outline-none w-full"
                    />
                    <span className="text-gray-500">-</span>
                    <input
                        type="date"
                        value={completeEndDate}
                        onChange={(e) => setCompleteEndDate(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded-md p-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 outline-none w-full"
                    />
                </div>
            </div>

            <div className="flex flex-col gap-1 w-full md:w-[200px]">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Project
                </label>
                <select
                    value={project}
                    onChange={(e) => setProject(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 outline-none"
                >
                    <option value="">All Project</option>
                    {projects.map((p) => (
                        <option key={p.id} value={p.name}>
                            {p.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="flex flex-col gap-1 w-full md:w-[150px]">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Job Number
                </label>
                <input
                    type="text"
                    placeholder="Job Number"
                    value={jobNumber}
                    onChange={(e) => setJobNumber(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded-md p-1.5 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 outline-none w-full"
                />
            </div>

            <div className="flex flex-col gap-1 w-full md:w-[150px]">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Other Number
                </label>
                <input
                    type="text"
                    placeholder="Other Number"
                    value={otherNumber}
                    onChange={(e) => setOtherNumber(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded-md p-1.5 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 outline-none w-full"
                />
            </div>

            <div className="flex flex-col gap-1 w-full md:w-[150px]">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    District
                </label>
                <select
                    value={district}
                    onChange={(e) => setDistrict(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 outline-none"
                >
                    <option value="">All District</option>
                    {districts.map((d) => (
                        <option key={d.id} value={d.name}>
                            {d.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="flex flex-col gap-1 w-full md:w-[150px]">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Job Type
                </label>
                <select
                    value={jobType}
                    onChange={(e) => setJobType(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 outline-none"
                >
                    <option value="">All</option>
                    {jobTypes.map((jt) => (
                        <option key={jt.id} value={jt.name}>
                            {jt.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="flex flex-col gap-1 w-full md:w-[150px]">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Status
                </label>
                <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 outline-none"
                >
                    <option value="">All</option>
                    {jobStatuses.map((js) => (
                        <option key={js.id} value={js.name}>
                            {js.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="flex flex-col gap-1 w-full md:w-[250px]">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Pole Type
                </label>
                <Select
                    isMulti
                    options={poleTypeOptions}
                    value={selectedPoleTypes}
                    onChange={(val) => setSelectedPoleTypes(val as any[])}
                    styles={{
                        menu: (provided) => ({ ...provided, zIndex: 9999 }),
                        control: (provided) => ({
                            ...provided,
                            minHeight: '38px',
                            borderRadius: '0.375rem',
                            borderColor: '#d1d5db',
                            fontSize: '0.875rem'
                        }),
                    }}
                    placeholder="All Pole Type"
                />
            </div>

            <div className="flex flex-col gap-1 w-full md:w-[250px]">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Pole Status
                </label>
                <Select
                    isMulti
                    options={poleStatusOptions}
                    value={selectedPoleStatuses}
                    onChange={(val) => setSelectedPoleStatuses(val as any[])}
                    styles={{
                        menu: (provided) => ({ ...provided, zIndex: 9999 }),
                        control: (provided) => ({
                            ...provided,
                            minHeight: '38px',
                            borderRadius: '0.375rem',
                            borderColor: '#d1d5db',
                            fontSize: '0.875rem'
                        }),
                    }}
                    placeholder="All Pole Status"
                />
            </div>

            <div className="flex gap-2 ml-auto lg:ml-0">
                <button
                    onClick={handleSearch}
                    className="bg-[#03543f] hover:bg-[#24644e] text-white px-6 py-2 rounded-md text-sm font-medium transition-colors"
                >
                    Search
                </button>
                <button
                    onClick={handleReset}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 px-6 py-2 rounded-md text-sm font-medium transition-colors"
                >
                    Reset
                </button>
            </div>
        </div>
    );
};

export default PoleManagementFilters;
